import { Button, Checkbox, Dropdown } from "antd";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import MultipleFilter from "./Filters/multiple-filter";
import Period from "./Filters/period";
import SingleFilter from "./Filters/single-filter";
import { Icon } from "../icon/icon";

/**
 * =========================
 * Shared helpers
 * =========================
 */
const buildGroupedMaps = (filtersBy = []) => {
  const isGrouped = Array.isArray(filtersBy) && filtersBy[0]?.items;

  const flatFiltersBy = isGrouped
    ? filtersBy.flatMap((g) => g.items || [])
    : filtersBy;

  const nameCountMap = {};
  if (isGrouped) {
    filtersBy.forEach((group) => {
      (group.items || []).forEach((item) => {
        nameCountMap[item.name] = (nameCountMap[item.name] || 0) + 1;
      });
    });
  }

  const filterIdToLabelMap = {};
  if (isGrouped) {
    filtersBy.forEach((group) => {
      (group.items || []).forEach((item) => {
        const shouldAppendGroup = nameCountMap[item.name] > 1;
        filterIdToLabelMap[item.id] = shouldAppendGroup
          ? `${item.name} (${group.groupName})`
          : item.name;
      });
    });
  }

  return {
    isGrouped,
    flatFiltersBy,
    filterIdToLabelMap,
  };
};

const buildGeneralFiltersMenu = ({
  filtersBy,
  flatFiltersBy,
  isGrouped,
  filters,
}) => {
  const getDropdownItem = (key, label) => ({
    key: key || label,
    // LF360-1981: for the browser tests; a group heading has no key and no id.
    ...(key ? { "data-testid": `filters-option-${key}` } : {}),
    label: key ? (
      <div
        key={key || label}
        style={{
          display: "flex",
          width: "100%",
          alignItems: "center",
          cursor: "pointer",
          margin: "5px 0",
        }}
        role="presentation"
      >
        <Checkbox checked={!!filters[key]} />
        <span style={{ marginLeft: 10 }}>{label}</span>
      </div>
    ) : (
      <span key={label} style={{ fontWeight: 700, color: "#000" }}>
        {label}
      </span>
    ),
    disabled: !key,
  });

  if (!isGrouped) {
    return flatFiltersBy.map((item) => getDropdownItem(item.id, item.name));
  }

  return filtersBy.flatMap((group) => {
    const groupLabel = getDropdownItem(null, group.groupName);
    const groupItems = (group.items || []).map((item) =>
      getDropdownItem(item.id, item.name)
    );
    return [groupLabel, ...groupItems];
  });
};

const renderActiveFilters = ({
  filters,
  flatFiltersBy,
  filterIdToLabelMap,
  isGrouped,
  lastAddedFilter,
  readOnly,
  updateFilters,
}) => {
  const applyFilters = Object.keys(filters || {});

  return applyFilters.map((f) => {
    const element = flatFiltersBy.find((el) => el.id === f);
    const isLastAdded = lastAddedFilter === f;
    if (!element) return null;

    const label =
      isGrouped && filterIdToLabelMap[element.id]
        ? filterIdToLabelMap[element.id]
        : element.name;

    const fieldWithLabel = { ...element, name: label };

    if (element.period) {
      return (
        <Period
          key={element.id}
          field={fieldWithLabel}
          values={filters[element.id]?.[filters[element.id].length - 1]}
          onChange={(key, value) => updateFilters(key, value)}
          autoOpen={isLastAdded}
        />
      );
    }

    if (element.children) {
      return (
        <MultipleFilter
          key={element.id}
          field={fieldWithLabel}
          options={element.children}
          values={filters[element.id] || []}
          onChange={(key, value) => updateFilters(key, value)}
          autoOpen={isLastAdded}
          readOnly={readOnly}
        />
      );
    }

    return (
      <SingleFilter
        key={element.id}
        field={fieldWithLabel}
        values={filters[element.id] || []}
        onChange={(key, value) => updateFilters(key, value)}
        autoOpen={isLastAdded}
      />
    );
  });
};

/**
 * =========================
 * LEGACY VERSION
 * Не чіпаємо поведінку.
 * =========================
 */
const LegacyDropdownFilters = ({
  filtersBy = [],
  updateFilterValue,
  filter = {},
  solidBorder = false,
  readOnly = false,
}) => {
  const { categoryId } = useParams();

  const [filters, setFilters] = useState(filter);
  const [lastAddedFilter, setLastAddedFilter] = useState(null);

  const { isGrouped, flatFiltersBy, filterIdToLabelMap } = useMemo(
    () => buildGroupedMaps(filtersBy),
    [filtersBy]
  );

  useEffect(() => {
    if (updateFilterValue) {
      updateFilterValue(filters);
    }
  }, [filters, updateFilterValue]);

  useEffect(() => {
    setFilters({});
  }, [categoryId]);

  useEffect(() => {
    setFilters(filter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateFilters = (key, value) => {
    if (readOnly) return;

    if (value === undefined) {
      const filtersKeys = Object.keys(filters);
      if (filtersKeys.includes(key)) {
        delete filters[key];
        setFilters({ ...filters });
      } else {
        setFilters({ ...filters, [key]: [] });
        setLastAddedFilter(key);
      }
      return;
    }

    const hasValueInFilter = filters[key]?.includes?.(value);

    if (hasValueInFilter) {
      const result = filters[key].filter((item) => item !== value);
      setFilters({ ...filters, [key]: result });
    } else {
      setFilters({ ...filters, [key]: [...(filters[key] || []), value] });
    }
  };

  const generalFilters = buildGeneralFiltersMenu({
    filtersBy,
    flatFiltersBy,
    isGrouped,
    filters,
  });

  const activeFilters = renderActiveFilters({
    filters,
    flatFiltersBy,
    filterIdToLabelMap,
    isGrouped,
    lastAddedFilter,
    readOnly,
    updateFilters,
  });

  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginTop: 5 }}>
      {activeFilters}
      <Dropdown
        disabled={readOnly}
        menu={{
          items: generalFilters,
          onClick: (e) => {
            updateFilters(e.key);
          },
        }}
        trigger={["click"]}
      >
        <Button
          type="text"
          style={{
            color: "var(--text-2)",
            border: solidBorder ? "1px solid #6C6C8929" : "none",
          }}
          icon={<Icon name={"add"} color={"text-2"} />}
          data-testid="filters-add"
        >
          Add filter
        </Button>
      </Dropdown>
    </div>
  );
};

/**
 * =========================
 * CONTROLLED VERSION
 * Окрема логіка + optional leading
 * =========================
 */
const ControlledDropdownFilters = ({
  filtersBy = [],
  updateFilterValue,
  filter = {},
  solidBorder = false,
  readOnly = false,
  leading = null,
  savedViewsMode = false,
}) => {
  const [lastAddedFilter, setLastAddedFilter] = useState(null);

  const filters = filter || {};

  const { isGrouped, flatFiltersBy, filterIdToLabelMap } = useMemo(
    () => buildGroupedMaps(filtersBy),
    [filtersBy]
  );

  const updateFilters = (key, value) => {
    if (readOnly) return;

    const nextFilters = { ...(filters || {}) };

    if (value === undefined) {
      if (Object.prototype.hasOwnProperty.call(nextFilters, key)) {
        delete nextFilters[key];
      } else {
        nextFilters[key] = [];
        setLastAddedFilter(key);
      }

      updateFilterValue?.(nextFilters);
      return;
    }

    const currentValues = Array.isArray(nextFilters[key]) ? nextFilters[key] : [];
    const hasValueInFilter = currentValues.includes(value);

    if (hasValueInFilter) {
      nextFilters[key] = currentValues.filter((item) => item !== value);
    } else {
      nextFilters[key] = [...currentValues, value];
    }

    updateFilterValue?.(nextFilters);
  };

  const generalFilters = buildGeneralFiltersMenu({
    filtersBy,
    flatFiltersBy,
    isGrouped,
    filters,
  });

  const activeFilters = renderActiveFilters({
    filters,
    flatFiltersBy,
    filterIdToLabelMap,
    isGrouped,
    lastAddedFilter,
    readOnly,
    updateFilters,
  });

  const hasAnyActiveFilters = Object.keys(filters || {}).length > 0;

  return (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "flex-start",
        gap: 10,
        width: "100%",
        minWidth: 0,
      }}
    >
      {leading ? (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            flex: "0 0 auto",
          }}
        >
          {leading}
        </div>
      ) : null}

      {activeFilters}

      <Dropdown
        disabled={readOnly}
        menu={{
          items: generalFilters,
          onClick: (e) => {
            updateFilters(e.key);
          },
        }}
        trigger={["click"]}
      >
        <Button
          type="text"
          style={{
            color: "var(--text-2)",
            border: solidBorder ? "1px solid #6C6C8929" : "none",
            flex: "0 0 auto",
          }}
          icon={<Icon name={"add"} color={"text-2"} />}
          data-testid="filters-add"
        >
          {savedViewsMode && hasAnyActiveFilters ? "" : "Add filters"}
        </Button>
      </Dropdown>
    </div>
  );
};

/**
 * =========================
 * SWITCHER
 * =========================
 */
const DropdownFilters = (props) => {
  const { mode = "legacy" } = props;

  if (mode === "controlled") {
    return <ControlledDropdownFilters {...props} />;
  }

  return <LegacyDropdownFilters {...props} />;
};

export default DropdownFilters;